import {endpointKeys} from '@common/api'
import {ReplaceSlotsForDayRequest} from '@generated/models'
import type {QueryKey} from '@tanstack/react-query'
import {
  PartnerServiceResponse as PartnerService,
  PractitionerAvailabilityResponse,
  DayAggregatedRangesResponse,
  DayTimeRangesResponse,
} from '@common/models'
import {
  partnerServicesNormaliser,
  practitionerAvailabilityNormaliser,
} from '@common/normalisers'
import {useContext} from 'react'

import {useApi, useWebGet, useWebSet} from './helpers'

import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

// Partner services list
export const useGetPartnerServices = () =>
  useWebGet<PartnerService[]>(
    [endpointKeys.partnerAdminPartnerServices],
    useApi().partnerAdminPartnerServices(undefined),
    partnerServicesNormaliser,
  )

// Aggregated day-time-ranges (no practitioner/date)
export const useGetAggregatedDayTimeRanges = (
  partnerServiceId?: string,
  options?: {enabled?: boolean},
) =>
  useWebGet<DayAggregatedRangesResponse[]>(
    [
      endpointKeys.partnerAdminPartnerServiceDayTimeRanges,
      {partnerServiceId: partnerServiceId || 'placeholder'},
    ],
    useApi().partnerAdminPartnerServiceDayTimeRanges({
      partnerServiceId: partnerServiceId || 'placeholder',
    }),
    (data) => data,
    {enabled: !!partnerServiceId && (options?.enabled ?? true)},
  )

// Detail day-time-ranges for a practitioner and date
export const useGetDayTimeRangesForDay = (
  params: {partnerServiceId: string; practitionerId: string; date: string},
  options?: {enabled?: boolean},
) =>
  useWebGet<DayTimeRangesResponse>(
    [endpointKeys.partnerAdminPartnerServiceDayTimeRanges, params],
    useApi().partnerAdminPartnerServiceDayTimeRanges(params),
    (data) => data,
    {enabled: options?.enabled},
  )

// Practitioner availability for a day
export const useGetPractitionerAvailability = (
  params: {practitionerId: string; date: string},
  options?: {enabled?: boolean},
) =>
  useWebGet<PractitionerAvailabilityResponse>(
    [endpointKeys.partnerAdminPractitionerAvailability, params],
    useApi().partnerAdminPractitionerAvailability(params),
    practitionerAvailabilityNormaliser,
    {enabled: options?.enabled},
  )

// Replace all slots for the day
export const useReplaceSlotsForDay = (
  partnerServiceId: string,
  detail?: {practitionerId: string; date: string},
) => {
  const {addNotification} = useContext(NotificationContext)
  return useWebSet<ReplaceSlotsForDayRequest>(
    [endpointKeys.partnerAdminPartnerServiceReplaceDay, {partnerServiceId}],
    useApi().partnerAdminPartnerServiceReplaceDay({partnerServiceId}),
    'PUT',
    undefined,
    [
      [
        endpointKeys.partnerAdminPartnerServiceDayTimeRanges,
        {partnerServiceId},
      ],
      // Always expire any cached availability
      [endpointKeys.partnerAdminPractitionerAvailability],
      // Invalidate availability for the edited practitioner/date
      ...(detail
        ? ([
            [endpointKeys.partnerAdminPractitionerAvailability, detail],
          ] as QueryKey[])
        : ([] as QueryKey[])),
      ...(detail
        ? ([
            [
              endpointKeys.partnerAdminPartnerServiceDayTimeRanges,
              {
                partnerServiceId,
                practitionerId: detail.practitionerId,
                date: detail.date,
              },
            ],
          ] as QueryKey[])
        : ([] as QueryKey[])),
    ],
    // Disable default generic error toast; handle success and error locally per call
    {
      onError: () => undefined,
      onSuccess: (data) => {
        const result = (data || {}) as {
          created?: number
          reactivated?: number
          cancelled?: number
        }
        const created = result.created ?? 0
        const reactivated = result.reactivated ?? 0
        const cancelled = result.cancelled ?? 0

        if (created > 0 || reactivated > 0) {
          addNotification(
            'Records created successfully',
            NotificationType.Success,
          )
          return
        }
        if (created === 0 && reactivated === 0 && cancelled > 0) {
          addNotification(
            'Records deleted successfully',
            NotificationType.Warning,
          )
          return
        }
        addNotification('No changes were made.', NotificationType.Warning)
      },
    },
  )
}
